import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Button from './Button';
import TextInput from './TextInput';
import Modal from './Modal';
import ErrorMessage from './ErrorMessage';
import { logger } from '../utils/logger';

interface Category {
  _id: string;
  name: string;
  description?: string;
  icon?: string;
}

interface Props {
  familyGroupId: string;
  onCategoryChange?: () => void;
}

const CategoryManager: React.FC<Props> = ({ familyGroupId, onCategoryChange }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', icon: '' });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/family-groups/${familyGroupId}/categories`);
      setCategories(response.data);
      setError(null);
    } catch (err: any) {
      logger.error('Error fetching categories:', err);
      setError(err.response?.data?.message || 'Failed to fetch categories');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [familyGroupId]);

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      setIsLoading(true);
      await api.post(`/family-groups/${familyGroupId}/categories`, newCategory);
      setNewCategory({ name: '', description: '', icon: '' });
      setIsAddModalOpen(false);
      fetchCategories();
      if (onCategoryChange) onCategoryChange();
    } catch (err: any) {
      logger.error('Error creating category:', err);
      setError(err.response?.data?.message || 'Failed to create category');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!selectedCategory) return;
    if (!selectedCategory.name.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      setIsLoading(true);
      await api.patch(
        `/family-groups/${familyGroupId}/categories/${selectedCategory._id}`,
        {
          name: selectedCategory.name,
          description: selectedCategory.description,
          icon: selectedCategory.icon
        }
      );
      setIsEditModalOpen(false);
      setSelectedCategory(null);
      fetchCategories();
      if (onCategoryChange) onCategoryChange();
    } catch (err: any) {
      logger.error('Error updating category:', err);
      setError(err.response?.data?.message || 'Failed to update category');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      setIsLoading(true);
      await api.delete(`/family-groups/${familyGroupId}/categories/${categoryId}`);
      fetchCategories();
      if (onCategoryChange) onCategoryChange();
    } catch (err: any) {
      logger.error('Error deleting category:', err);
      setError(err.response?.data?.message || 'Failed to delete category');
    } finally {
      setIsLoading(false);
    }
  };

  const iconOptions = [
    { value: 'box', label: '📦 Box' },
    { value: 'food', label: '🍎 Food' },
    { value: 'clothing', label: '👕 Clothing' },
    { value: 'electronics', label: '💻 Electronics' },
    { value: 'tools', label: '🔧 Tools' },
    { value: 'books', label: '📚 Books' },
    { value: 'toys', label: '🧸 Toys' },
    { value: 'furniture', label: '🪑 Furniture' },
    { value: 'misc', label: '🔍 Miscellaneous' }
  ];

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Categories</h2>
        <Button onClick={() => setIsAddModalOpen(true)}>Add Category</Button>
      </div>

      {error && (
        <ErrorMessage
          message={error}
          className="mb-4"
          onDismiss={() => setError(null)}
        />
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {isLoading && <div className="p-4 text-center">Loading categories...</div>}
        {!isLoading && categories.length === 0 && (
          <div className="p-4 text-center text-gray-500">No categories found. Create one to get started.</div>
        )}
        <ul className="divide-y divide-gray-200">
          {categories.map(category => (
            <li key={category._id} className="px-4 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-xl mr-2">
                  {category.icon ? getIconForCategory(category.icon) : '📦'}
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900">{category.name}</p>
                  {category.description && (
                    <p className="text-sm text-gray-500">{category.description}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    setSelectedCategory(category);
                    setIsEditModalOpen(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => handleDeleteCategory(category._id)}
                >
                  Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Add Category Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
        }}
        title="Add Category"
        actions={{
          primary: {
            label: 'Create Category',
            onClick: handleCreateCategory,
            disabled: isLoading
          },
          secondary: {
            label: 'Cancel',
            onClick: () => {
              setIsAddModalOpen(false);
            },
            disabled: isLoading
          }
        }}
        afterLeave={() => {
          setNewCategory({ name: '', description: '', icon: '' });
          setError(null);
        }}
        className="relative z-50"
      >
        <div className="space-y-4">
          {error && (
            <ErrorMessage message={error} onDismiss={() => setError(null)} />
          )}
          <TextInput
            label="Name"
            value={newCategory.name}
            onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
            required
            placeholder="Enter category name"
          />
          <TextInput
            label="Description"
            value={newCategory.description || ''}
            onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
            placeholder="Enter category description (optional)"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icon
            </label>
            <select
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              value={newCategory.icon || ''}
              onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
            >
              <option value="">Select an icon</option>
              {iconOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Modal>

      {/* Edit Category Modal */}
      {selectedCategory && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
          }}
          title="Edit Category"
          actions={{
            primary: {
              label: 'Save Changes',
              onClick: handleUpdateCategory,
              disabled: isLoading
            },
            secondary: {
              label: 'Cancel',
              onClick: () => {
                setIsEditModalOpen(false);
              },
              disabled: isLoading
            }
          }}
          afterLeave={() => {
            setSelectedCategory(null);
            setError(null);
          }}
          className="relative z-50"
        >
          <div className="space-y-4">
            {error && (
              <ErrorMessage message={error} onDismiss={() => setError(null)} />
            )}
            <TextInput
              label="Name"
              value={selectedCategory.name}
              onChange={(e) => setSelectedCategory({ ...selectedCategory, name: e.target.value })}
              required
              placeholder="Enter category name"
            />
            <TextInput
              label="Description"
              value={selectedCategory.description || ''}
              onChange={(e) => setSelectedCategory({ ...selectedCategory, description: e.target.value })}
              placeholder="Enter category description (optional)"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Icon
              </label>
              <select
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                value={selectedCategory.icon || ''}
                onChange={(e) => setSelectedCategory({ ...selectedCategory, icon: e.target.value })}
              >
                <option value="">Select an icon</option>
                {iconOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Helper function to convert icon code to emoji
function getIconForCategory(icon: string): string {
  switch (icon) {
    case 'box': return '📦';
    case 'food': return '🍎';
    case 'clothing': return '👕';
    case 'electronics': return '💻';
    case 'tools': return '🔧';
    case 'books': return '📚';
    case 'toys': return '🧸';
    case 'furniture': return '🪑';
    case 'misc': return '🔍';
    default: return '📦';
  }
}

export default CategoryManager; 